import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertTriangle, ArrowLeft, Check, Clock, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/Card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import useAuthStore from '@/store/useAuthStore'
import useGetBillingSubscription, {
  billingSubscriptionQueryKey,
} from '@/services/api/billing/useGetBillingSubscription'
import useGetPlans, { type Plan } from '@/services/api/billing/useGetPlans'
import {
  useCreateCheckoutSession,
  useCreatePortalSession,
} from '@/services/api/billing/useBillingSessions'
import { useQueryClient } from '@tanstack/react-query'

function daysUntil(date: string | null): number | null {
  if (!date) return null
  const diff = new Date(date).getTime() - Date.now()
  return diff <= 0 ? 0 : Math.ceil(diff / 86_400_000)
}

function formatPrice(plan: Plan): string {
  if (plan.amount == null) return ''
  const amount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: plan.currency.toUpperCase(),
  }).format(plan.amount / 100)
  const period =
    plan.interval_count === 1
      ? plan.interval
      : `${plan.interval_count} ${plan.interval}s`
  return `${amount} / ${period}`
}

export default function SubscriptionPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const currentTeamId = useAuthStore((s) => s.currentTeamId)

  const { data, isLoading, refetchForce } = useGetBillingSubscription({
    teamId: currentTeamId,
  })
  const checkout = useCreateCheckoutSession()
  const portalSession = useCreatePortalSession()

  const hasActive = data?.has_active_subscription
  const sub = data?.subscription ?? null
  const checkoutHandled = useRef(false)

  const { data: plans, isLoading: plansLoading } = useGetPlans({
    enabled: !isLoading && !hasActive,
  })

  // Handle the return from Stripe Checkout.
  useEffect(() => {
    if (checkoutHandled.current) return
    const params = new URLSearchParams(location.search)
    const status = params.get('status')
    if (!status) return
    checkoutHandled.current = true

    if (status === 'success') {
      toast.success('Subscription activated. Your plan will update shortly.')
      queryClient.invalidateQueries({
        queryKey: billingSubscriptionQueryKey(currentTeamId),
      })
      refetchForce()
    } else if (status === 'canceled') {
      toast.info('Checkout canceled — you have not been charged.')
    }
    navigate(location.pathname, { replace: true })
  }, [location, navigate, queryClient, refetchForce, currentTeamId])

  const startCheckout = async (planKey?: string) => {
    try {
      const { url } = await checkout.mutateAsync({ teamId: currentTeamId, planKey })
      window.location.href = url
    } catch {
      toast.error('Could not start checkout')
    }
  }

  const openPortal = async () => {
    try {
      const { url } = await portalSession.mutateAsync(currentTeamId)
      window.location.href = url
    } catch {
      toast.error('Could not open the billing portal')
    }
  }

  const trialDays = sub?.is_trialing ? daysUntil(sub.trial_ends_at) : null
  const isPastDue = sub?.status === 'past_due'

  return (
    <div className="mx-auto max-w-3xl p-6">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back
      </button>

      <h1 className="mb-1 text-2xl font-semibold">Subscription</h1>
      <p className="mb-6 text-muted-foreground">
        Manage your plan and billing for this space.
      </p>

      {isLoading ? (
        <Card className="space-y-3 p-6">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-9 w-48" />
        </Card>
      ) : hasActive ? (
        <div className="space-y-4">
          {trialDays !== null && (
            <Card className="flex items-center gap-3 border-primary/30 bg-primary/5 p-4">
              <Clock className="size-5 shrink-0 text-primary" />
              <div className="flex-1">
                <p className="font-medium">
                  {trialDays === 0
                    ? 'Your trial ends today'
                    : `${trialDays} day${trialDays === 1 ? '' : 's'} left in your trial`}
                </p>
                <p className="text-sm text-muted-foreground">
                  Add a payment method to keep your access.
                </p>
              </div>
              <Button size="sm" onClick={openPortal}>
                Upgrade
              </Button>
            </Card>
          )}

          {isPastDue && (
            <Card className="flex items-center gap-3 border-destructive/40 bg-destructive/5 p-4">
              <AlertTriangle className="size-5 shrink-0 text-destructive" />
              <div className="flex-1">
                <p className="font-medium">Payment failed</p>
                <p className="text-sm text-muted-foreground">
                  Update your payment method to restore full access.
                </p>
              </div>
              <Button size="sm" variant="destructive" onClick={openPortal}>
                Fix payment
              </Button>
            </Card>
          )}

          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Current plan</p>
                <p className="text-lg font-semibold">{sub?.plan_name}</p>
              </div>
              <span
                className={`rounded px-2 py-1 text-xs font-medium capitalize ${
                  sub?.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {sub?.status?.replace('_', ' ')}
              </span>
            </div>

            <p className="mb-4 text-sm text-muted-foreground">
              {sub?.is_trialing
                ? `Trial ends ${sub.trial_ends_at ? new Date(sub.trial_ends_at).toLocaleDateString() : '—'}`
                : sub?.current_period_end
                  ? `Renews ${new Date(sub.current_period_end).toLocaleDateString()}`
                  : ''}
            </p>

            <Button
              onClick={openPortal}
              disabled={portalSession.isPending}
              className="w-full"
            >
              <ExternalLink className="mr-2 size-4" />
              {portalSession.isPending
                ? 'Opening…'
                : 'Manage billing in Stripe'}
            </Button>
          </Card>

          <PlanFeatureList features={data?.features ?? []} />
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Choose a plan</h2>
          {plansLoading ? (
            <Card className="p-6">
              <Skeleton className="h-24 w-full" />
            </Card>
          ) : (plans?.length ?? 0) === 0 ? (
            <Card className="p-6 text-sm text-muted-foreground">
              No plans are available right now.
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {plans!.map((plan) => (
                <Card key={plan.plan_key} className="flex flex-col gap-3 p-6">
                  <div>
                    <p className="text-base font-semibold">{plan.name}</p>
                    {plan.description && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {plan.description}
                      </p>
                    )}
                  </div>
                  <p className="text-xl font-semibold">{formatPrice(plan)}</p>
                  <Button
                    className="mt-auto"
                    onClick={() => startCheckout(plan.plan_key)}
                    disabled={checkout.isPending}
                  >
                    {checkout.isPending ? 'Redirecting…' : 'Subscribe'}
                  </Button>
                </Card>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            You&apos;ll be taken to Stripe&apos;s secure checkout to enter payment
            details.
          </p>
        </div>
      )}
    </div>
  )
}

function PlanFeatureList({
  features,
}: {
  features: {
    lookup_key: string
    name: string
    available: boolean
    value: string | null
    fe_visibility: 'lock' | 'hide'
  }[]
}) {
  const visible = features.filter((f) => f.fe_visibility !== 'hide')
  if (visible.length === 0) return null

  return (
    <Card className="p-6">
      <h3 className="mb-3 font-medium">Features</h3>
      <Separator className="mb-3" />
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {visible.map((f) => (
          <li
            key={f.lookup_key}
            className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm ${
              f.available ? '' : 'opacity-50'
            }`}
          >
            <span className="flex items-center gap-1.5">
              {f.available && <Check className="size-3.5 text-green-600" />}
              {f.name}
            </span>
            <span className="text-muted-foreground">
              {f.available ? (f.value ?? '') : 'Locked'}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  )
}
